import React, { createContext, useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useProfile } from '../hooks/useAuth';
import { getUserToken, getUserDetail, logoutUser } from '../util/util';
import { queryKeys } from '../lib/react-query';
import secureLocalStorage from 'react-secure-storage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const token = getUserToken();
  
  // Get user profile from React Query
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser,
  } = useProfile();

  // Check if user is authenticated
  const isAuthenticated = !!token && !!user;
  const isLoading = isLoadingUser && !!token;

  // Initialize user data from local storage if available
  useEffect(() => {
    const storedUser = getUserDetail();
    if (storedUser && !user && token) {
      // Set cached user data from localStorage to avoid loading state
      // Normalize to the same shape returned by the profile API: { success, data }
      queryClient.setQueryData(queryKeys.auth.user, { success: true, data: storedUser });
      // Ensure we fetch the latest profile from the server (so fields like Ghana Card populate immediately)
      queryClient.refetchQueries({ queryKey: queryKeys.auth.user, exact: true });
    }
  }, [token, user, queryClient]);

  // Handle authentication errors
  useEffect(() => {
    if (userError && token) {
      // If there's an error fetching user data and we have a token,
      // it might be expired or invalid
      console.error('Authentication error:', userError);
      
      // Don't automatically logout on network errors, only on auth errors
      if (userError?.response?.status === 401) {
        logoutUser();
      }
    }
  }, [userError, token]);

  // Login function
  const login = async (credentials) => {
    // This will be handled by the useLogin hook
    // The hook will automatically update the cache and localStorage
    return Promise.resolve();
  };

  // Logout function
  const logout = async () => {
    try {
      // Clear React Query cache
      queryClient.clear();
      
      // Clear localStorage
      secureLocalStorage.removeItem('token');
      secureLocalStorage.removeItem('refreshToken');
      secureLocalStorage.removeItem('user');
      secureLocalStorage.removeItem('expiresAt');
      
      // Redirect to login
      window.location.href = '/onboard/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if there's an error
      logoutUser();
    }
  };

  // Update user in cache
  const updateUser = (userData) => {
    // Keep cache consistent with profile API shape
    queryClient.setQueryData(queryKeys.auth.user, { success: true, data: userData });
    secureLocalStorage.setItem('user', JSON.stringify(userData));
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  // Check if user has specific role
  const hasRole = (role) => {
    if (!user || !user.roles) return false;
    return user.roles.includes(role);
  };

  // Refresh user data
  const refreshUser = () => {
    return refetchUser();
  };

  const value = {
    // Auth state
    user,
    isAuthenticated,
    isLoading,
    token,
    
    // Auth methods
    login,
    logout,
    updateUser,
    refreshUser,
    
    // Permission methods
    hasPermission,
    hasRole,
    
    // Utils
    error: userError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
